import type {
    ImAccount,
    MoneyMapRuleWithAccount,
    MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import React from 'react';
import { ArrowStraight, YStack } from 'smoothie';

import {
    type BankAccountDisplayDetail,
    type DepositAccount,
    type IncomeSplitConfig,
    type PaycheckRoutingRuleAccountMetadata,
} from '../types/IncomeSplit';
import { getAccountFromRuleNew } from '../utils/incomeSplitUtilsNew';
import IncomeSplitRuleSetReviewCard from './IncomeSplitRuleSetReviewCard';

// Read only list of rules
export const IncomeSplitRulesListNew = ({
    incomeSplit,
    bankAccountDetails,
    externalAccounts,
    internalInvestmentAccounts,
    moneyMapRulesWithProposedAccounts,
    moneyMap,
    isMoneySystem = false,
}: {
    incomeSplit: IncomeSplitConfig;
    bankAccountDetails:
        | PaycheckRoutingRuleAccountMetadata[]
        | BankAccountDisplayDetail[]
        | DepositAccount[];
    externalAccounts?: DepositAccount[];
    internalInvestmentAccounts?: ImAccount[];
    moneyMapRulesWithProposedAccounts?: MoneyMapRuleWithAccount[];
    moneyMap?: MoneyMapWithRulesAndAccounts;
    isMoneySystem?: boolean;
}) => {
    const sortedGroups = incomeSplit.ruleSets.sort(
        (a, b) => a.priority - b.priority,
    );

    const groupsWithAccounts = sortedGroups
        .map((group) => {
            const rulesWithAccounts = group.rules
                .sort((a, b) => {
                    return (b.percentage ?? 0) - (a.percentage ?? 0);
                })
                .map((rule) => {
                    const account = getAccountFromRuleNew({
                        rule,
                        bankAccountDetails,
                        externalAccounts: externalAccounts ?? [],
                        moneyMapRulesWithProposedAccounts:
                            moneyMapRulesWithProposedAccounts ?? [],
                        internalInvestmentAccounts:
                            internalInvestmentAccounts ?? [],
                    });
                    return { rule, account };
                })
                .filter(({ account }) => account);

            return { group, hasAccounts: rulesWithAccounts.length > 0 };
        })
        .filter(({ hasAccounts }) => hasAccounts);

    return (
        <YStack gap={8} flexGrow={1} flexShrink={1}>
            {groupsWithAccounts.map(({ group }, index) => {
                const isLastGroup = index === groupsWithAccounts.length - 1;
                return (
                    <YStack key={group.name ?? `group-${index}`} gap={8}>
                        <IncomeSplitRuleSetReviewCard
                            group={group}
                            index={index}
                            bankAccountDetails={bankAccountDetails}
                            externalAccounts={externalAccounts ?? []}
                            internalInvestmentAccounts={
                                internalInvestmentAccounts ?? []
                            }
                            moneyMapRulesWithProposedAccounts={
                                moneyMapRulesWithProposedAccounts
                            }
                            context="review"
                            moneyMap={moneyMap}
                            isMoneySystem={isMoneySystem}
                        />
                        {!isLastGroup && (
                            <YStack alignSelf="center" padding={8}>
                                <ArrowStraight />
                            </YStack>
                        )}
                    </YStack>
                );
            })}
        </YStack>
    );
};
